import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

interface LocationInputProps {
  location: string;
  onLocationChange: (location: string) => void;
  radius: number; // in miles
  onRadiusChange: (radius: number) => void;
}

const LocationInput: React.FC<LocationInputProps> = ({ 
  location, 
  onLocationChange, 
  radius, 
  onRadiusChange 
}) => {
  const [useCurrentLocation, setUseCurrentLocation] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // Handle getting current location
  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      setIsLoading(true);
      setUseCurrentLocation(true);
      
      navigator.geolocation.getCurrentPosition(
        (position) => {
          // Convert lat/long to a readable address using reverse geocoding
          // For now, we'll just use the coordinates
          const coords = `${position.coords.latitude},${position.coords.longitude}`;
          onLocationChange(coords);
          setIsLoading(false);
        },
        (error) => {
          console.error('Error getting location:', error);
          setIsLoading(false);
          setUseCurrentLocation(false);
          alert('Unable to retrieve your location. Please enter it manually.');
        }
      );
    } else {
      alert('Geolocation is not supported by this browser.');
    }
  };

  const handleLocationInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUseCurrentLocation(false);
    onLocationChange(e.target.value);
  };

  // Radius values for slider
  const radiusOptions = [0.5, 1, 2, 5, 10];
  
  return (
    <LocationContainer>
      <InputGroup>
        <LocationLabel>Location</LocationLabel>
        <InputWrapper>
          <Input
            type="text"
            placeholder="Enter your location"
            value={location}
            onChange={handleLocationInput}
            disabled={isLoading}
          />
          <LocationButton 
            onClick={getCurrentLocation}
            disabled={isLoading}
          >
            {isLoading ? '...' : '📍'}
          </LocationButton>
        </InputWrapper>
      </InputGroup>

      <RadiusGroup>
        <RadiusLabel>Distance: {radius} mile{radius !== 1 ? 's' : ''}</RadiusLabel>
        <SliderContainer>
          <RadiusSlider
            type="range"
            min="0"
            max="4"
            value={radiusOptions.indexOf(radius)}
            onChange={(e) => onRadiusChange(radiusOptions[parseInt(e.target.value)])}
            step="1"
          />
          <TickMarks>
            {radiusOptions.map((value) => (
              <TickMark key={value} isActive={radius >= value}>
                {value}
              </TickMark>
            ))}
          </TickMarks>
        </SliderContainer>
      </RadiusGroup>
    </LocationContainer>
  );
};

const LocationContainer = styled.div`
  margin-bottom: 1.5rem;
`;

const InputGroup = styled.div`
  margin-bottom: 1rem;
`;

const LocationLabel = styled.label`
  display: block;
  font-size: 1.1rem;
  margin-bottom: 0.75rem;
  color: var(--text-color);
  font-weight: 500;
`;

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const Input = styled.input`
  flex: 1;
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 8px 0 0 8px;
  font-size: 1rem;
  background: var(--card-bg);
  outline: none;
  transition: border-color 0.2s;
  
  &:focus {
    border-color: var(--primary-color);
  }
  
  &:disabled {
    background: #f2f2f2;
    cursor: not-allowed;
  }
`;

const LocationButton = styled.button`
  padding: 0.75rem 1rem;
  background: var(--secondary-color);
  color: white;
  border: none;
  border-radius: 0 8px 8px 0;
  cursor: pointer;
  font-size: 1.1rem;
  
  &:hover {
    background: var(--primary-color);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const RadiusGroup = styled.div`
  margin-top: 1.5rem;
`;

const RadiusLabel = styled.label`
  display: block;
  font-size: 1.1rem;
  margin-bottom: 1rem;
  color: var(--text-color);
  font-weight: 500;
  display: flex;
  justify-content: space-between;
`;

const SliderContainer = styled.div`
  padding: 0 10px;
`;

const RadiusSlider = styled.input`
  width: 100%;
  height: 5px;
  background: #ddd;
  border-radius: 5px;
  outline: none;
  -webkit-appearance: none;
  margin-bottom: 1.25rem;
  
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: var(--primary-color);
    cursor: pointer;
    border: 2px solid white;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  }
`;

const TickMarks = styled.div`
  display: flex;
  justify-content: space-between;
`;

const TickMark = styled.span<{ isActive: boolean }>`
  font-size: 0.8rem;
  color: ${props => props.isActive ? 'var(--primary-color)' : '#999'};
  font-weight: ${props => props.isActive ? 'bold' : 'normal'};
`;

export default LocationInput;